import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronSmall } from '../../assets/svg';
import {
  LocationPayoutService,
  PayoutConfigResponse,
} from '../../types/location.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { CollapsibleComponent, Div, Icon } from '../../styles/styles';
import { Disclaimer, P } from '../../styles/text';
import { ConfigPatch, PayoutConfigFields } from './PayoutConfigFields';
import {
  describePayoutConfig,
  isPayoutServiceVisible,
  payoutModelLabel,
  payoutServiceLabel,
} from './payoutConfig.helpers';

/** Matches the `transition` on CollapsibleComponent. */
const COLLAPSE_MS = 300;

// The lib is also consumed by the Next apps, where layout effects don't run.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Tracks the natural height of collapsible content. `CollapsibleComponent`
 * animates `max-height`, so the number has to stay in sync as tiers are added
 * or removed and as switching models shows/hides whole sections - measuring
 * once would pin it to the first render and clip everything added later.
 */
const useMeasuredHeight = <T extends HTMLElement>() => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);

  // Deliberately no dependency array: any re-render can change the content, and
  // measuring in a layout effect updates max-height before the browser paints.
  // Re-setting the same number is a no-op, so this can't loop.
  useIsomorphicLayoutEffect(() => {
    if (ref.current) setHeight(ref.current.scrollHeight);
  });

  // Catches resizes that don't re-render this component (viewport, font load).
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setHeight(element.scrollHeight));
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, height };
};

const PayoutConfigRow = ({
  config,
  savedConfig,
  expanded,
  onToggle,
  onChange,
  disabled,
}: {
  config: PayoutConfigResponse;
  savedConfig?: PayoutConfigResponse;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: ConfigPatch) => void;
  disabled?: boolean;
}) => {
  const { ref: contentRef, height: contentHeight } =
    useMeasuredHeight<HTMLDivElement>();

  /*
   * The provider select opens an absolutely positioned menu, which the
   * collapsible's own overflow:hidden slices. Lifting the clip is only safe
   * once the box already fits its content - mid-animation the content would
   * spill outside the row - so it goes back on for the length of the
   * max-height transition after every height change, this one included.
   * A timer rather than `transitionend`: `transition: all` fires that event
   * per property and not at all in a background tab, which would leave the
   * clip stuck on.
   */
  const [clipLifted, setClipLifted] = useState(false);

  useEffect(() => {
    setClipLifted(false);
    if (!expanded) return undefined;

    const timer = setTimeout(() => setClipLifted(true), COLLAPSE_MS + 50);

    return () => clearTimeout(timer);
  }, [expanded, contentHeight]);

  return (
    <Div $box $flex $column $p="16px">
      <Div $pointer $flex $alignCenter $between $gap="12px" onClick={onToggle}>
        <Div>
          <P $bold $m="0">
            {payoutServiceLabel(config)}
          </P>
          <Disclaimer $m="0">
            {payoutModelLabel(config.model)} · {describePayoutConfig(config)}
          </Disclaimer>
        </Div>
        <Icon $rotate={expanded ? '180deg' : '0'}>
          <ChevronSmall />
        </Icon>
      </Div>

      {/*
        The collapsible clips whatever leaves its box - that is how the
        max-height animation works - and the rule cards inside are raised
        surfaces, so its edge would slice their shadows. Stretching it into
        the row's own padding and putting the inset back on the content keeps
        every field exactly where it was and gives the shadows room to land.
      */}
      <CollapsibleComponent
        expanded={expanded}
        elementHeight={contentHeight}
        style={{
          marginLeft: '-16px',
          marginRight: '-16px',
          overflow: clipLifted ? 'visible' : 'hidden',
        }}
      >
        <Div ref={contentRef} $p="16px 16px 24px 16px">
          <PayoutConfigFields
            config={config}
            savedConfig={savedConfig}
            onChange={onChange}
            disabled={disabled}
          />
        </Div>
      </CollapsibleComponent>
    </Div>
  );
};

/**
 * Per-service payout editor. Holds no state of its own beyond which rows are
 * open - the parent owns `configs` and submits them.
 */
export const PayoutConfigEditor = ({
  configs,
  savedConfigs,
  onChange,
  disabled,
}: {
  configs: PayoutConfigResponse[];
  /** The same services as the API has them, before any edit in this form. */
  savedConfigs?: PayoutConfigResponse[];
  onChange: (configs: PayoutConfigResponse[]) => void;
  disabled?: boolean;
}) => {
  const [expanded, setExpanded] = useState<LocationPayoutService[]>([]);

  const toggle = (service: LocationPayoutService) =>
    setExpanded((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );

  const patchConfig = (service: LocationPayoutService, patch: ConfigPatch) =>
    onChange(
      configs.map((config) =>
        config.service === service ? { ...config, ...patch } : config
      )
    );

  return (
    <Div $flex $column $gap="8px">
      {configs
        .filter((config) => isPayoutServiceVisible(config.service))
        .map((config) => (
          <PayoutConfigRow
            key={config.service}
            config={config}
            savedConfig={savedConfigs?.find(
              (saved) => saved.service === config.service
            )}
            disabled={disabled}
            expanded={expanded.includes(config.service)}
            onToggle={() => toggle(config.service)}
            onChange={(patch) => patchConfig(config.service, patch)}
          />
        ))}
    </Div>
  );
};
